import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import StarIcon from "@material-ui/icons/Star";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";

import { getHuntersReports } from "./api";
import NumberFormat from "react-number-format";
import Progress from "components/UI/Progress";
import Layout from "../../components/layout";
import Filters from "./Filters";
import styleConstants from "../../constants/styleConstants";
import EmptyOrShow from "../../components/EmptyOrShow";
import Search from "../../components/UI/Search";
import cookiesService from "../../services/cookiesService";
import { MODALS } from "../../constants";
import { HuntersAccordion } from "./accordion";
import { RopKpiTable } from "./RopTable";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setShowModal } from "../../services/redux/actions/app-actions";
import {
  clampToV2Month,
  hunterDetailsPath,
  readStoredV2Period,
  writeStoredV2Period,
} from "./cutover";

const cookiesServiceInstance = cookiesService.getService();

const HuntersKpiWrapper = () => {
  const { position_code } = cookiesServiceInstance.getUserData();
  const history = useHistory();

  if (
    !position_code ||
    ["CALL_CENTER", "CALL_CENTER_HEAD", "SALE_ARCHIVE"].includes(
      position_code
    )
  ) {
    history.push("/");
    return null;
  }
  return <HuntersKpi />;
};

// Фильтр ОП зафиксирован своим отделом для SELLER/SALE_HEAD (TZ раздел 5.6) — этим ролям
// backend всё равно подставит request.user.sale_department_id, но UI не должен слать
// произвольный хардкод и должен согласованно отражать реальный отдел пользователя.
const defaultSaleDepartmentId = (position_code, sale_department_id) => {
  if (position_code === "SALE_HEAD_HEAD" || position_code === "SALE_AUDITOR") {
    return "";
  }
  return sale_department_id || "";
};

const initialV2Filters = (position_code, sale_department_id) => {
  const stored = readStoredV2Period();
  const clamped = clampToV2Month(
    stored.year || new Date().getFullYear(),
    stored.month || new Date().getMonth() + 1
  );
  return {
    year: String(clamped.year),
    month: String(clamped.month),
    sale_department_id: defaultSaleDepartmentId(
      position_code,
      sale_department_id
    ),
  };
};

const HuntersKpi = () => {
  const classes = useHuntersStyles();
  const dispatch = useDispatch();
  const { position_code, employee_id, sale_department_id } =
    cookiesServiceInstance.getUserData();
  const canManageGrades =
    position_code === "SALE_HEAD" || position_code === "SALE_HEAD_HEAD";

  const [search, setSearch] = useState("");
  const [stateSum, setStateSum] = useState({});
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState(() =>
    initialV2Filters(position_code, sale_department_id)
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    writeStoredV2Period(filters.year, filters.month);
  }, [filters.year, filters.month]);

  const handleSearchChange = useCallback(() => {
    if (!search) return setFilteredData(data);
    setFilteredData(sortData(data, search));
  }, [search, data]);

  useEffect(() => {
    const timeout = setTimeout(() => handleSearchChange(), 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [handleSearchChange]);

  useEffect(() => {
    setStateSum(calcSum(filteredData));
  }, [filteredData]);

  const fetchData = useCallback(() => {
    setLoading(true);
    getHuntersReports(filters)
      .then((res) => {
        setData(res);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = { ...filters, [name]: value };
    if (name === "year" || name === "month") {
      const clamped = clampToV2Month(next.year, next.month);
      next.year = String(clamped.year);
      next.month = String(clamped.month);
    }
    setFilters(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const openGradeModal = (e) => {
    dispatch(
      setShowModal(true, MODALS.changeGradeModal, {
        title: "Изменение грейда",
        hunterId: e.employee_id,
        hunterFio: e.fio,
        onSaved: fetchData,
      })
    );
  };

  return (
    <Layout title="KPI хантеров">
      <Box
        sx={{
          padding: "45px 20px 20px",
        }}
      >
        <Filters state={filters} handleChange={handleChange} />

        {position_code === "SALE_HEAD_HEAD" || position_code === "SALE_HEAD" ? (
          <HuntersAccordion filters={filters} />
        ) : null}
        {["SALE_HEAD_HEAD", "SALE_HEAD", "SALE_AUDITOR"].includes(
          position_code
        ) ? (
          <RopKpiTable filters={filters} />
        ) : null}
        <div style={{ marginBottom: "25px", width: "250px" }}>
          <Search
            placeholder={"Поиск"}
            handleChange={handleSearch}
            value={search}
          />
        </div>
        <EmptyOrShow loading={loading}>
          <TableContainer
            className={classes.table_container}
            style={
              position_code !== "SALE_HEAD_HEAD" ? { maxHeight: "80vh" } : {}
            }
          >
            <Table size="small" stickyHeader aria-label="sticky table">
              <TableHead className={classes.table_head}>
                <TableRow>
                  {headTitle.map((el) => {
                    return (
                      <TableCell key={el} style={{ fontWeight: "bold" }}>
                        {el}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody className={classes.table_body}>
                {filteredData.map((e) => {
                  return (
                    <TableRow
                      key={e.employee_id}
                      className={!e.with_plan ? classes.table_item_false : ""}
                    >
                      <TableCell align="left">
                        {e.sale_department_name}
                      </TableCell>
                      <TableCell align="left">
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          {e.employee_id === employee_id ||
                          position_code !== "SELLER" ? (
                            <Link
                              to={hunterDetailsPath(
                                e.employee_id,
                                filters.year,
                                filters.month
                              )}
                              target="_blank"
                            >
                              {e.fio}
                            </Link>
                          ) : (
                            e.fio
                          )}
                          {e.is_streak_star ? (
                            <Tooltip title={`Стабильность: план выполнен ${e.streak} мес. подряд`}>
                              <StarIcon
                                fontSize="small"
                                style={{ color: "#FFC107" }}
                              />
                            </Tooltip>
                          ) : null}
                          {e.is_fail_warning ? (
                            <Tooltip title={`Невыполнение плана ${e.fail_streak} мес. подряд`}>
                              <ReportProblemIcon
                                fontSize="small"
                                style={{ color: "#FF6262" }}
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          {e.hunter_type_name}
                          {canManageGrades ? (
                            <IconButton
                              size="small"
                              onClick={() => openGradeModal(e)}
                            >
                              <EditIcon
                                fontSize="small"
                                style={{ color: "#6191EF" }}
                              />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.conversion_total_cnt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.contracts_cnt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {`${e?.conversion_pct ?? 0}%`}
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.hunter_month_plan_sum}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.fact_opt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Progress
                          progress={e.percentage}
                          title="План"
                          color={e.percentage_color_code}
                        />
                        <Progress
                          progress={e.contract_percentage}
                          title="+ договор"
                          color={e.contract_percentage_color_code}
                          tooltip={
                            <NumberFormat
                              value={e?.fact2_opt}
                              displayType="text"
                              thousandSeparator={" "}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.left_sum}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.bonus_opt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.penalty_total_sum}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.team_bonus}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.total}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow className={classes.table_footer}>
                  <TableCell style={{ fontWeight: "bold" }}>Итого</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.conversion_total_cnt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.contracts_cnt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    {`${stateSum?.conversion_pct ?? 0}%`}
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.hunter_month_plan_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.fact_opt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell />
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.left_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.bonus_opt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.penalty_total_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.team_bonus}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.total}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </EmptyOrShow>
      </Box>
    </Layout>
  );
};

export default HuntersKpiWrapper;

const sortData = (data, search = "") => {
  return data.filter(
    (el) =>
      el?.fio?.toLowerCase().includes(search.toLowerCase()) ||
      el?.hunter_type_name?.toLowerCase().includes(search.toLowerCase()) ||
      el?.hunter_month_plan_sum
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.left_sum
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.total
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase())
  );
};

const calcSum = (filteredData) => {
  const contracts_cnt = filteredData?.reduce(
    (a, b) => a + (b.contracts_cnt || 0),
    0
  );
  const conversion_total_cnt = filteredData?.reduce(
    (a, b) => a + (b.conversion_total_cnt || 0),
    0
  );
  return {
    bonus_opt: filteredData?.reduce((a, b) => a + (b.bonus_opt || 0), 0),
    fact_opt: filteredData?.reduce((a, b) => a + (b.fact_opt || 0), 0),
    hunter_month_plan_sum: filteredData?.reduce(
      (a, b) => a + (b.hunter_month_plan_sum || 0),
      0
    ),
    left_sum: filteredData?.reduce((a, b) => a + (b.left_sum || 0), 0),
    penalty_total_sum: filteredData?.reduce(
      (a, b) => a + (b.penalty_total_sum || 0),
      0
    ),
    team_bonus: filteredData?.reduce((a, b) => a + (b.team_bonus || 0), 0),
    total: filteredData?.reduce((a, b) => a + (b.total || 0), 0),
    contracts_cnt,
    conversion_total_cnt,
    conversion_pct: conversion_total_cnt
      ? Math.round((contracts_cnt / conversion_total_cnt) * 1000) / 10
      : 0,
  };
};
const headTitle = [
  "Отдел продаж",
  "Хантер",
  "Тип",
  "Все",
  "Договор заключен",
  "Конверсия",
  "План",
  "Факт опционалки",
  "% выполнения плана",
  "Остаток плана",
  "Бонус опционалки",
  "Штраф",
  "Командный бонус",
  "Итог",
];

export const useHuntersStyles = makeStyles({
  table_container: {
    "@media (max-width: 1024px)": {
      maxHeight: "44vh",
    },
    "@media (min-width: 1440px)": {
      maxHeight: "55vh",
    },

    "@media (min-width: 2000px)": {
      maxHeight: "62vh",
    },
    "@media (min-width: 2200px)": {
      maxHeight: "68vh",
    },
    border: "1px solid #ccc",
    borderRadius: 4,
    backgroundColor: "#fff",
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: styleConstants.primaryColor,
      borderRadius: styleConstants.borderRadius,
    },
  },
  table_footer: {
    "& td": {
      backgroundColor: "#fff",
      borderTop: "1px solid #f0f2f6 !important",
      fontWeight: "bold",
      color: "black",
    },
    "& td span": {
      whiteSpace: "nowrap",
    },
  },
  table_item_false: {
    "& td:nth-child(1)": {
      borderLeft: "0.25rem solid rgba(220, 53, 69, 0.5) !important",
    },
  },

  table_head: {
    background: "white",
    "& th": {
      fontSize: "12px",
      fontWeight: 600,
      background: "white",
    },
  },
  table_body: {
    "& tr": {
      transition: "0.4s",
      "&:hover": {
        background: (props) => (props.hover === false ? "" : "#d3d3d36e"),
      },
    },
    "& td": {
      borderTop: "1px solid #ccc !important",
      borderBottom: "none !important",
      borderLeft: "none !important",
      borderRight: "none !important",
    },
    "& td span": {
      whiteSpace: "nowrap",
    },
  },
});
